import {
  enumZoneStatus,
  InventoryZoneSortOption,
  ZoneStatus,
  ZoneType,
} from '@rematter/frontend-lib-api-client';

import {
  InventoryZoneRecord,
  InventoryZoneSummaryStats,
} from '../types/inventoryZone';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const inventoryZonesQueryAtom = createQueryAtomFactory(
  'inventory.inventoryZones',
  (params: {
    searchTerm?: string;
    facilityId?: string;
    zoneType?: ZoneType[];
    status?: ZoneStatus[];
    parentZoneId?: string;
    limit?: number;
    offset?: number;
    sorting?: InventoryZoneSortOption[];
  }) => ({
    inventoryZones: {
      __args: {
        filter: {
          searchTerm: params.searchTerm,
          facilityId: params.facilityId,
          zoneType: params.zoneType,
          status: params.status,
          parentZoneId: params.parentZoneId,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        facilityId: true,
        facility: {
          id: true,
          facilityName: true,
        },
        parentZoneId: true,
        parentZone: {
          id: true,
          zoneCode: true,
          name: true,
        },
        zoneCode: true,
        name: true,
        zoneType: true,
        status: true,
        capacityValue: true,
        capacityUnit: true,
        description: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      zones: InventoryZoneRecord[];
      stats: InventoryZoneSummaryStats;
      totalCount: number;
    } => {
      if (!data || !data.inventoryZones || !data.inventoryZones.entries) {
        return {
          zones: [],
          stats: {
            totalZones: 0,
            activeZones: 0,
            closedZones: 0,
            archivedZones: 0,
          },
          totalCount: 0,
        };
      }

      const zones = data.inventoryZones.entries.map((zone) => ({
        id: zone.id,
        facilityId: zone.facilityId,
        facilityName: zone.facility?.facilityName || '',
        parentZoneId: zone.parentZoneId || null,
        parentZoneCode: zone.parentZone?.zoneCode || null,
        zoneCode: zone.zoneCode,
        name: zone.name,
        zoneType: zone.zoneType,
        status: zone.status,
        capacityValue: zone.capacityValue ? Number(zone.capacityValue) : null,
        capacityUnit: zone.capacityUnit || null,
        description: zone.description || null,
        createdAt: zone.timestamps?.createdAt || '',
        updatedAt: zone.timestamps?.updatedAt || '',
      }));

      const activeZones = zones.filter(
        (zone) => zone.status === enumZoneStatus.Active,
      ).length;
      const closedZones = zones.filter(
        (zone) => zone.status === enumZoneStatus.Closed,
      ).length;
      const archivedZones = zones.filter(
        (zone) => zone.status === enumZoneStatus.Archived,
      ).length;

      return {
        zones,
        stats: {
          totalZones: data.inventoryZones.page?.total || 0,
          activeZones,
          closedZones,
          archivedZones,
        },
        totalCount: data.inventoryZones.page?.total || 0,
      };
    },
  },
);

// Stable empty array to prevent infinite re-renders
const EMPTY_ZONES: {
  id: string;
  zoneCode: string;
  name: string;
  zoneType: string;
  parentZoneId: string | null;
}[] = [];

export const inventoryZonesByFacilityQueryAtom = createQueryAtomFactory(
  'inventory.inventoryZonesByFacility',
  (params: { facilityId: string }) => ({
    inventoryZones: {
      __args: {
        filter: {
          facilityId: params.facilityId,
          status: [enumZoneStatus.Active],
        },
        pagination: {
          limit: 1000,
          offset: 0,
        },
      },
      entries: {
        id: true,
        zoneCode: true,
        name: true,
        zoneType: true,
        parentZoneId: true,
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data) => {
      if (!data || !data.inventoryZones || !data.inventoryZones.entries) {
        return EMPTY_ZONES;
      }
      return data.inventoryZones.entries.map((zone) => ({
        id: zone.id,
        zoneCode: zone.zoneCode,
        name: zone.name,
        zoneType: zone.zoneType,
        parentZoneId: zone.parentZoneId || null,
      }));
    },
  },
);
